import React, { useEffect, useState, useMemo, useCallback } from 'react'
import shortid from 'shortid'
import classNames from 'classnames'

// import { Label } from './input'
import Button, { ButtonRow } from './button'
import styles from '../component-styles/collapsable-filters.module.scss'
import HeaderPanel, { Panel } from './header-panel'
import ToolTip from './tooltip'
// import { LabelSlot, FormRow, FieldSlot } from './row'
import { FormRow, FieldSlot } from './row'
import { MultiSelectBox } from './select-box'
import { Tag } from './add-multi-values-tags'
import { logAction } from '../api-client/analytics-client'
import { saveUserData } from '../local-client/local-client'
import { LoadingLabel } from './loader'

interface DimensionSelectorProps {
  item: any
  onChange: any
  defaultValue: string[]
}

export function DimensionSelector({
  item,
  onChange,
  defaultValue,
}: DimensionSelectorProps): React.ReactElement {
  const { dimensionName, dimensionOptions, helpText } = item
  const [value, setValue] = useState<string[]>([...defaultValue])
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  // <LabelSlot className={styles.labelSlot}>
  //   <Label className={styles.label} id={`select-${dimensionName}`}>
  //     {dimensionName}
  //   </Label>

  // </LabelSlot>

  return (
    <FormRow className={classNames(styles.formRow, styles.wrapper)} last>
      <FieldSlot className={styles.fieldSlot}>
        <MultiSelectBox
          inline
          inlineLabel={dimensionName}
          showOnlyButton
          className={styles.select}
          id={dimensionName}
          typeAhead
          value={value}
          placeholder="Select value(s)"
          items={dimensionOptions.map((i) => {
            return { optionName: i === '' ? '(empty)' : i, optionValue: i }
          })}
          onChange={(val: string[], parentID?: string): void => {
            setValue(val)
            onChange(val)
          }}
        />
        <ToolTip className={styles.tooltip} message={helpText} />
      </FieldSlot>
    </FormRow>
  )
}

interface CollapsableFiltersSummaryProps {
  data: any[]
  selectedDimensions: any
  onChange: (val: string[], item: any) => void
}

export function CollapsableFiltersSummary({
  data,
  onChange,
  selectedDimensions,
}: CollapsableFiltersSummaryProps): React.ReactElement | null {
  const filtered = data.filter((dimension) => {
    const { dimensionName, dimensionOptions } = dimension

    const hasValues = Object.prototype.hasOwnProperty.call(
      selectedDimensions,
      dimensionName,
    )
    const allSelected =
      hasValues &&
      selectedDimensions[dimensionName].length === dimensionOptions.length

    const noneSelected =
      hasValues && selectedDimensions[dimensionName].length === 0
    const doNotShow = (hasValues && allSelected) || noneSelected || !hasValues
    return !doNotShow
  })

  if (filtered.length === 0) {
    return null
  }

  return (
    <HeaderPanel className={styles.headerPanel}>
      <Panel>
        <ButtonRow
          className={classNames(styles.buttonRow, styles.buttonRowTags)}
        >
          <p className={styles.summaryTitle}>
            <strong>Filtered on:</strong>
          </p>
          {data.map((dimension) => {
            const { dimensionName, dimensionOptions } = dimension

            const hasValues = Object.prototype.hasOwnProperty.call(
              selectedDimensions,
              dimensionName,
            )
            const allSelected =
              hasValues &&
              selectedDimensions[dimensionName].length ===
                dimensionOptions.length
            const noneSelected =
              hasValues && selectedDimensions[dimensionName].length === 0

            if ((hasValues && allSelected) || noneSelected || !hasValues) {
              return null
            }
            if (!(hasValues && !allSelected)) {
              return null
            }
            return selectedDimensions[dimensionName].map((item) => {
              return (
                <Tag
                  key={shortid.generate()}
                  className={styles.tag}
                  compact
                  onClick={() => {
                    const currentSelection = selectedDimensions[dimensionName]
                    const newSelection = currentSelection.filter(
                      (i) => i !== item,
                    )
                    onChange(newSelection, dimension)
                  }}
                >
                  <span className={styles.tagTitle}>{dimensionName}:</span>{' '}
                  {item === '' ? '(empty)' : item}
                </Tag>
              )
            })
          })}
        </ButtonRow>
      </Panel>
    </HeaderPanel>
  )
}
interface SupportMetricDropDownProps {
  successMetricsList: any[]
  allAvailableMetrics: any[]
  setSuccessMetricsList: any
  accountID?: string
}

export function SupportMetricDropDown({
  successMetricsList,
  allAvailableMetrics,
  setSuccessMetricsList,
  accountID = '',
}: SupportMetricDropDownProps): React.ReactElement {
  const useAllAvailableMetrics = useMemo(() => {
    return allAvailableMetrics.filter((item) => item.setupComplete)
  }, [allAvailableMetrics])

  return (
    <MultiSelectBox
      disabled={useAllAvailableMetrics.length === 0}
      inline
      inlineLabel="Support metrics"
      showOnlyButton
      className={classNames(styles.successMetricSelector)}
      id="supportMetrics"
      // typeAhead
      value={successMetricsList}
      keyName="displayName"
      keyValue="metricID"
      items={useAllAvailableMetrics}
      onChange={(val: string[]): void => {
        setSuccessMetricsList(val)
        if (accountID) {
          saveUserData({ successMetricsList: val }, accountID)
        }
      }}
    />
  )
}

interface CollapsableFiltersProps {
  data: any[]
  selectedDimensions: any
  onChange: (val: string[], item: any) => void
  children?: React.ReactNode
  onResetFilters: () => void
  isMobile: boolean
  accountID?: string
}

export default function CollapsableFilters({
  data,
  onChange,
  onResetFilters,
  selectedDimensions,
  children,
  isMobile,
  accountID = '',
}: CollapsableFiltersProps): React.ReactElement {
  const [collapsed, setCollapsed] = useState(true)
  const hasFilters = useMemo(() => {
    return Object.keys(selectedDimensions).length > 0
  }, [selectedDimensions])

  const filtersButtons = useCallback(() => {
    let label = hasFilters ? `Edit filters` : `Apply filters`
    if (!collapsed) {
      label = 'Hide filters'
    }
    return (
      <Button
        className={styles.collapseButton}
        type="pinkBorder"
        align="right"
        onClick={async () => {
          if (collapsed) {
            setCollapsed(false)
            const actionData = {
              action: 'edit-filters',
              longDescription: '',
              websiteSection: 'report',
            }
            await logAction(actionData)
          } else {
            setCollapsed(true)
          }
        }}
      >
        {label}
      </Button>
    )
  }, [hasFilters, collapsed])

  if (collapsed) {
    return (
      <>
        <div className={classNames(styles.box)}>
          <div className={styles.topRowBox}>
            <div className={styles.topRow}>
              {children}
              {!isMobile && (
                <div className={styles.buttonInlineWrapper}>
                  {filtersButtons()}
                </div>
              )}
            </div>
          </div>
          {isMobile && (
            <HeaderPanel className={styles.headerPanel}>
              <Panel className={styles.panel}>
                <ButtonRow className={styles.buttonRow}>
                  <div className={styles.fullRow}>
                    <div className={styles.filterButtonsWrapper}>
                      {filtersButtons()}
                    </div>
                  </div>
                </ButtonRow>
              </Panel>
            </HeaderPanel>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={classNames(
          styles.box,
          styles.topRowWrapper,
          styles.expandedBox,
        )}
      >
        <div className={styles.topRowBox}>
          <div className={styles.topRow}>
            {children}
            {!isMobile && (
              <div className={styles.buttonInlineWrapper}>
                {filtersButtons()}
              </div>
            )}
          </div>
        </div>
        <HeaderPanel className={styles.headerPanel}>
          <Panel className={styles.panel}>
            <ButtonRow className={styles.buttonRow}>
              <div className={styles.fullRow}>
                <div className={styles.filterButtonsWrapper}>
                  {isMobile && filtersButtons()}
                </div>
              </div>
              {data.length === 0 && (
                <LoadingLabel
                  label="Loading filters"
                  className={styles.loader}
                  fixedWidth
                />
              )}
              {data.map((item) => {
                const hasValues = Object.prototype.hasOwnProperty.call(
                  selectedDimensions,
                  item.dimensionName,
                )

                return (
                  <DimensionSelector
                    defaultValue={
                      !hasValues ? [] : selectedDimensions[item.dimensionName]
                    }
                    item={item}
                    key={JSON.stringify(item)}
                    onChange={(val: string[]) => {
                      onChange(val, item)
                    }}
                  />
                )
              })}
              {data.length !== 0 && (
                <div className={styles.wrapper}>
                  <Button
                    className={styles.resetButton}
                    type="clear"
                    align="right"
                    onClick={() => {
                      onResetFilters()
                    }}
                  >
                    Reset filters
                  </Button>
                </div>
              )}
            </ButtonRow>
          </Panel>
        </HeaderPanel>
      </div>
    </>
  )
}
